import './widget.css';

import { IonRippleEffect, IonToggle, isPlatform } from '@ionic/react';
import { FieldConfig, useField } from 'formik';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { IonItem } from '../../../../../components/ionic/IonItem';
import { useKeyboardIsShowing } from '../../../../../theme/keyboard.hook';
import { useWidgetClasses } from '../../hook/widget.hooks';
import { WidgetLabel } from './widget-label';
import {
  SwitchWidgetProps,
  WidgetRef,
  createWidgetExtension,
} from './widget.types';

interface SwitchInputProps extends SwitchWidgetProps {}

const SwitchInput = (props: SwitchInputProps & FieldConfig): ReactElement => {
  const {
    label,
    description,
    showDescription,
    disabled,
    ionItemProps,
    extensions,
    subtitle,
    subtitleColor,
    isNested,
    isPaywalled,
    transparentBackground,
    ...otherProps
  } = props;

  const { className: ionItemClassName, ...restIonItemProps } =
    ionItemProps ?? {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [field, meta, helper] = useField(otherProps as any);

  const inputRef = useRef<HTMLIonToggleElement>(null);
  const [isDisabled, setIsDisabled] = useState(disabled ?? false);
  const isKeyboardShowing = useKeyboardIsShowing();

  const rightElement = useMemo(() => {
    return createWidgetExtension(extensions?.rightElement, {
      field: [field, meta, helper],
      widgetRef: inputRef.current as WidgetRef,
      setDisabled: setIsDisabled,
      isDisabled,
    });
  }, [extensions?.rightElement, field, helper, isDisabled, meta]);

  useEffect(() => {
    if (disabled !== undefined) {
      setIsDisabled(disabled);
    }
  }, [disabled]);

  const classList = useWidgetClasses({
    isNested,
    transparentBackground,
    isRippleUtilized: true,
    isPaywalled,
  });

  const isIOS = isPlatform('ios');

  const ionLabelClass = isNested ? 'ion-padding-start' : undefined;

  return (
    <IonItem
      className={`${classList} ${ionItemClassName || ''}`.trim()}
      lines="full"
      disabled={isDisabled}
      onTouchStart={() => {
        if (isKeyboardShowing) {
          const el = document.activeElement as HTMLInputElement;
          if (typeof el?.blur === 'function') {
            el?.blur();
          }
        }
      }}
      onClick={async () => {
        if (!isIOS) {
          inputRef.current?.click();
        }
      }}
      button={rightElement ? false : !isIOS}
      {...restIonItemProps}
    >
      <IonToggle
        aria-label={label}
        data-cy={otherProps.name}
        justify="space-between"
        ref={inputRef}
        disabled={isDisabled}
        checked={field.value}
        onIonChange={(e) => {
          if (isPaywalled) {
            return;
          }

          helper.setValue(e.detail.checked);
        }}
        onClick={(e) => {
          if (isPaywalled) {
            return false;
          }
          e.stopPropagation();
        }}
      >
        <WidgetLabel
          label={label}
          description={description}
          showDescription={showDescription}
          error={meta.error}
          forceDisable={isDisabled}
          subtitle={subtitle}
          subtitleColor={subtitleColor}
          ionLabelProps={{
            className: ionLabelClass,
          }}
        />
      </IonToggle>
      {rightElement}
      {!isIOS && <IonRippleEffect />}
    </IonItem>
  );
};

export { SwitchInput };
